import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useTrackVisibility } from 'react-intersection-observer-hook';
// API
import { getAwardWinningBooks } from 'api/company';
// Components
import Head from 'components/metatags_helmet/metatags';
import TitleWithLine from 'components/titleWithLine';
import ItemListAwardedBook from 'components/ItemListAwardedBook/item';
import PaginationLoader from 'components/loader/PaginationLoader';
// Icons
import { FaRedo } from 'react-icons/fa';
//styles
import './awardWinningBooksPage.scss';

const PAGE_SIZE = 10;

const goUp = () => {
  window.scrollTo(0, 0);
};

const AwardWinningBooksPage = () => {
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [maxPage, setMaxPage] = useState(null);
  const [ref, { isVisible }] = useTrackVisibility();
  const { t } = useTranslation(['home']);
  const title = t('librosPremiados', { ns: 'backoffice' });
  const [yearFilter, setYearFilter] = useState('');

  const fetchAwards = useCallback(
    async (pageNum = 1) => {
      setLoading(true);
      try {
        const params = { page: pageNum, page_size: PAGE_SIZE };
        if (yearFilter) {
          params.year = yearFilter;
        }
        const res = await getAwardWinningBooks(params);
        if (res.status === 200) {
          const results = res.data.results || [];
          const processedAwards = [];
          results.forEach((award) => {
            if (award.products && award.products.length > 0) {
              award.products.forEach((product) => {
                processedAwards.push({ award, product });
              });
            }
          });
          setAwards((prev) => (pageNum === 1 ? processedAwards : [...prev, ...processedAwards]));
          setMaxPage(res.data.total_pages ?? null);
          setNextPage(res.data.next ?? null);
        }
      } catch (error) {
        console.error('Error fetching awards:', error);
        if (pageNum === 1) setAwards([]);
      } finally {
        setLoading(false);
      }
    },
    [yearFilter],
  );

  useEffect(() => {
    setAwards([]);
    setPage(1);
    fetchAwards(1);
    goUp();
  }, [yearFilter, fetchAwards]);

  useEffect(() => {
    if (isVisible && page < maxPage && nextPage && awards.length > 0 && !loading) {
      const nextPageNum = page + 1;
      fetchAwards(nextPageNum);
      setPage(nextPageNum);
    }
    // Solo debe reaccionar al scroll (sentinel visible), no a page/maxPage/etc para evitar bucles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleYearChange = (e) => {
    setYearFilter(e.target.value);
  };

  const handleReset = () => {
    setYearFilter('');
  };

  return (
    <Container fluid className="margin-top-md px-4 d-flex mainDiv">
      <Head title={title} noIndex={true} />
      <Row className="awards">
        <Col lg={12}>
          <div className="title-filter-container">
            <div className="title-wrapper">
              <TitleWithLine text={title} />
            </div>
            <div className="filter-controls">
              <Form.Select className="custom-select-responsive mb-5" value={yearFilter} onChange={handleYearChange}>
                <option value="">{t('filterByYear', { ns: 'backoffice' })}</option>
                {[...Array(10)].map((_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </Form.Select>
              {yearFilter && (
                <Button 
                  variant="outline-secondary" 
                  className="reset-button mb-5"
                  onClick={handleReset}
                  title={t('reset', { ns: 'backoffice' }) || 'Resetear filtro'}
                >
                  <FaRedo />
                </Button>
              )}
            </div>
          </div>
        </Col>
        {loading && awards.length === 0 ? (
          <Col lg={12} className="text-center mt-4">
            <PaginationLoader visibility={loading} />
          </Col>
        ) : awards.length > 0 ? (
          <>
            {awards.map((item, index) => (
              <Col key={`${item.award.id}-${item.product.id}-${index}`} lg={2} className="margin-md">
                <ItemListAwardedBook award={item.award} product={item.product} />
              </Col>
            ))}
            {nextPage && !loading && (
              <Col lg={12}>
                <div aria-hidden="true" ref={ref} />
              </Col>
            )}
            {awards.length > 0 && (
              <Col lg={12} className="text-center">
                <PaginationLoader visibility={loading} />
              </Col>
            )}
          </>
        ) : (
          <Col lg={12} className="text-center mt-4">
            <h3 className="text-muted">{t('thereAreNoBooks', { ns: 'backoffice' })}</h3>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default AwardWinningBooksPage;